import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, CardMedia, Typography, Container } from "@mui/material";
import { useParams } from "react-router-dom";
import Colors from "assets/Style/Colors";
import { ErrorToaster } from "components/Toaster";
import { CommaSeparator } from "utils";
import LogoImage from "assets/Images/header_voucher.svg";

import {
  FontFamily,
  Images,
  InvoiceGlobal,
  InvoiceLocation,
  InvoiceMail,
  InvoicePhone,
} from "assets";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import { QRCodeCanvas } from "qrcode.react";
import SystemServices from "services/System";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const useStyle = makeStyles({
  headingBg: {
    margin: "30px 0px",
    padding: "12px 0px",
    textAlign: "center",
  },
  heading: {
    color: Colors.white,
    textTransform: "uppercase",
    fontWeight: 300,
    fontFamily: FontFamily.NunitoRegular,
  },
  text: {
    color: Colors.smokeyGrey,
    fontWeight: 300,
    fontFamily: FontFamily.NunitoRegular,
  },
  textColor: {
    color: Colors.green,
    fontWeight: 300,
    fontFamily: FontFamily.NunitoRegular,
  },
  tableCell: {
    backgroundColor: Colors.aliceBlue,
    border: "0.25px solid #D9D9D9",
    "& .MuiTypography-root": {
      padding: "2px 8px",
    },
  },
  header: {
    backgroundColor: "#0095C8",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
    padding: "12px 0",
    fontSize: "18px",
  },
  tablesCell: {
    padding: "8px 15px",
    borderBottom: "1px solid #B5B5B5",
    fontFamily: "Arial, sans-serif",
  },
  computationRow: {
    backgroundColor: "#F3F3F3",
    padding: "8px",
  },
  computationHeader: {
    backgroundColor: "#0095C8",
    color: "#fff",
    padding: "12px 0",
    textAlign: "center",
    fontSize: "18px",
  },
  gridItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  totalRow: {
    backgroundColor: "#e5f5f9",
    fontWeight: "bold",
  },
});

function PaymentVoucherInvoice({
  voucherDetail,
  voucherAccountDetail,
  onDone,
  v2,
}) {
  const { id } = useParams();
  const contentRef = useRef(null);
  const classes = useStyle();

  const [exchangeRateUsd, setExchangeRateUsd] = useState();
  const [layoutSelection, setLayoutSelection] = useState({
    text: "A4",
    value: "size-a4",
  });

  const getLastPathSegment = (path) => {
    const segments = path.split("→");
    return segments[segments.length - 1]?.trim();
  };

  const getCurrencies = async () => {
    try {
      let params = {
        detailed: true,
      };
      const { data } = await SystemServices.getCurrencies(params);

      setExchangeRateUsd(data?.currencies[2]?.conversion_rate);
    } catch (error) {
      ErrorToaster(error);
    }
  };
  useEffect(() => {
    if (voucherDetail) {
      getCurrencies();
    }
  }, [voucherDetail]);

  const hasAccountDetail = voucherAccountDetail.length > 0;

  useEffect(() => {
    if (!voucherDetail || exchangeRateUsd == null) return;

    setTimeout(async () => {
      const node = contentRef.current;
      if (!node) return;

      await document.fonts.ready;

      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#fff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.7);

      const isV2 = v2 === true;

      const sizeV1 = [999.9, 1136.88];
      const sizeV2 = [999.9, 1000.32];
      const pdfSize = isV2 ? sizeV2 : sizeV1;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: pdfSize,
        compress: true,
      });

      pdf.addImage(imgData, "PNG", 0, 0, pdfSize[0], pdfSize[1]);

      const blob = pdf.output("blob");
      const file = new File(
        [blob],
        `paymentVoucherV1-GRV-${voucherDetail?.id}.pdf`,
        { type: "application/pdf" }
      );

      onDone?.(file);
    }, 600);
  }, [exchangeRateUsd]);

  return (
    <Container>
      <div ref={contentRef}>
        <Box className={`pdf-page ${layoutSelection.value}`}>
          {hasAccountDetail ? (
            <>
              <Box>
                <Grid container spacing={0}>
                  <Box
                    sx={{
                      width: { xs: "100%", md: "25%" },
                      "@media print": {
                        width: "25%",
                        flex: "0 0 25%",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={LogoImage}
                      alt="Ship Logo"
                      sx={{
                        width: "100%",
                        objectFit: "contain",
                        display: "block",
                        maxHeight: "120px",
                      }}
                    />
                  </Box>

                  {/* Right Contact Info */}
                  <Box
                    sx={{
                      // bgcolor: Colors.primary,
                      backgroundColor: "#0C6135 ",
                      padding: 2,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      width: { xs: "100%", md: "75%" },
                      "@media print": {
                        width: "75%",
                        flex: "0 0 75%",
                      },
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <InvoicePhone />
                          <Typography
                            variant="body1"
                            sx={{
                              color: Colors.whiteSmoke,
                              fontFamily: FontFamily.openSans,
                              fontSize: 14,
                            }}
                          >
                            +971 6 510 2000
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <InvoiceMail />
                          <Typography
                            variant="body1"
                            sx={{
                              color: Colors.whiteSmoke,
                              fontFamily: FontFamily.openSans,
                              fontSize: 14,
                            }}
                          >
                            info@galaxyshipping.com
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <InvoiceGlobal />
                          <Typography
                            variant="body1"
                            sx={{
                              color: Colors.whiteSmoke,
                              fontFamily: FontFamily.openSans,
                              fontSize: 14,
                            }}
                          >
                            https://galaxyshipping.com
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <InvoiceLocation />
                          <Typography
                            variant="body1"
                            sx={{
                              color: Colors.whiteSmoke,
                              fontFamily: FontFamily.openSans,
                              fontSize: 14,
                            }}
                          >
                            Ind Area#4 P.O Box 83126, Sharjah, UAE
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Box
                  sx={{
                    mt: 5,
                    mb: 0.5,
                    position: "relative",
                    // backgroundColor: '#0C6135 ',
                    bgcolor: Colors.primary,
                    width: 1,
                    height: "12px",
                  }}
                >
                  <Typography
                    component={"span"}
                    variant="h2"
                    sx={{
                      fontFamily: "Helvetica",
                      color: Colors.charcoalGrey,
                      bgcolor: Colors.white,
                      pl: 2,
                      pr: 2,
                      pt: 2,
                      letterSpacing: "3px",
                      position: "absolute",
                      right: "90px",
                      top: "-40px",
                    }}
                  >
                    PAYMENT VOUCHER
                  </Typography>
                </Box>

                <Grid container sx={{ mt: "65px" }}>
                  <Grid item xs={6} sx={{ px: 2, py: 0.5 }}>
                    <Grid container sx={{ padding: "10px" }}>
                      <Grid item xs={5}>
                        <Typography variant="body1">Paid To:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {voucherDetail?.paid_to}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body1">Posted By:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {voucherDetail?.creator?.name}
                        </Typography>
                      </Grid>
                      {voucherDetail?.reversed_by && (
                        <>
                          <Grid item xs={5}>
                            <Typography variant="body1">
                              Reversed By:
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              {voucherDetail?.reversed_by}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      <Grid item xs={5}>
                        <Typography variant="body1">
                          {voucherDetail?.payment_medium == "bank" ? (
                            <>Bank Account</>
                          ) : (
                            <>Cashier Accounts</>
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {voucherDetail?.bank && (
                            <>{voucherDetail?.bank?.name}</>
                          )}

                          {!voucherDetail?.bank && (
                            <>
                              {voucherDetail?.cashier_aed && (
                                <>{voucherDetail?.cashier_aed?.name}</>
                              )}
                              {voucherDetail?.cashier_usd && (
                                <>{voucherDetail?.cashier_usd?.name}</>
                              )}
                            </>
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={6} sx={{ px: 2, py: 1 }}>
                    <Grid container sx={{ padding: "10px" }}>
                      <Grid item xs={5}>
                        <Typography variant="body1">Voucher #:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          GPV-{voucherDetail?.id}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body1">Branch:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {voucherDetail?.branch?.name ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body1">Processed On:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {moment(voucherDetail?.created_at).format(
                            "DD-MMM-YYYY hh:mm"
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body1">Date:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: FontFamily.NunitoRegular }}
                        >
                          {moment(voucherDetail?.c_date).format(
                            "DD-MMM-YYYY hh:mm"
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    sx={{
                      paddingRight: "30px",
                      paddingLeft: "30px",
                      paddingBottom: "15px",
                    }}
                  >
                    <Grid
                      container
                      sx={{
                        border: "1px solid #ddd",
                        marginTop: "4px",
                        marginLeft: "15px",
                        fontFamily: "Helvetica",
                      }}
                    >
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                          }}
                        >
                          <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                margin: 0,
                                padding: 0,
                                tableLayout: "fixed",
                              }}
                            >
                              <thead>
                                <tr
                                  style={{
                                    background:
                                      "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                                    color: Colors.whiteSmoke,
                                    fontWeight: "bold",
                                  }}
                                >
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      fontSize: "12px",
                                      width: "30px",
                                    }}
                                  >
                                    Sr.#
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    COA
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      fontSize: "12px",
                                      width: "100px",
                                    }}
                                  >
                                    Amount (AED)
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      fontSize: "12px",
                                      width: "550px",
                                    }}
                                  >
                                    Description
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {voucherAccountDetail.map((item, index) => (
                                  <>
                                    <tr key={item?.id}>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                          borderBottom: "1px solid #ddd",
                                          borderRight: "1px solid #ddd",
                                          borderLeft: "1px solid #ddd",
                                          fontSize: "12px",
                                          width: "30px",
                                        }}
                                      >
                                        {index + 1}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "left",
                                          borderBottom: "1px solid #ddd",
                                          borderRight: "1px solid #ddd",
                                          fontSize: "12px",
                                        }}
                                      >
                                        {getLastPathSegment(item?.full_path)}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                          borderBottom: "1px solid #ddd",
                                          borderRight: "1px solid #ddd",
                                          fontSize: "12px",
                                          width: "100px",
                                        }}
                                      >
                                        {item?.fcy_amount}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                          borderBottom: "1px solid #ddd",
                                          borderRight: "1px solid #ddd",
                                          fontSize: "12px",
                                          width: "550px",
                                        }}
                                      >
                                        {item?.description}
                                      </td>
                                    </tr>
                                  </>
                                ))}
                              </tbody>
                            </table>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{
                      paddingRight: "20px",
                      paddingLeft: "20px",
                      paddingTop: "20px",
                      paddingBottom: "5px",
                    }}
                  >
                    <Grid
                      container
                      sx={{
                        border: "1px solid #ddd",
                        marginTop: "5px",
                        marginLeft: "15px",
                        fontFamily: "Helvetica",
                      }}
                    >
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Helvetica",
                            textAlign: "center",
                            fontWeight: "bold",
                            padding: "8px",
                            background:
                              "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                            color: Colors.whiteSmoke,
                          }}
                        >
                          Paid Currency Details
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                          }}
                        >
                          <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                margin: 0,
                                padding: 0,
                                tableLayout: "fixed",
                              }}
                            >
                              <thead>
                                <tr
                                  style={{
                                    // background:
                                    //   "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                                    // color: Colors.whiteSmoke,
                                    fontWeight: "bold",
                                  }}
                                >
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Currency
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    FCY
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Ex.Rate
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    LCY
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"AED"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {voucherDetail?.aed_amount_fcy
                                      ? CommaSeparator(
                                          parseFloat(
                                            voucherDetail?.aed_amount_fcy
                                          )?.toFixed(2)
                                        )
                                      : "0.00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {CommaSeparator(parseFloat(1)?.toFixed(2))}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {voucherDetail?.aed_amount_lcy
                                      ? CommaSeparator(
                                          parseFloat(
                                            voucherDetail?.aed_amount_lcy
                                          )?.toFixed(2)
                                        )
                                      : "0.00"}
                                  </td>
                                </tr>
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {"USD"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {voucherDetail?.usd_amount_fcy
                                      ? CommaSeparator(
                                          parseFloat(
                                            voucherDetail?.usd_amount_fcy
                                          )?.toFixed(2)
                                        )
                                      : "0.00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {voucherDetail?.updated_ex_rate
                                      ? parseFloat(
                                          voucherDetail?.updated_ex_rate
                                        ).toFixed(3)
                                      : "0.00"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {voucherDetail?.usd_amount_lcy
                                      ? CommaSeparator(
                                          parseFloat(
                                            voucherDetail?.usd_amount_lcy
                                          )
                                        )
                                      : "0.00"}
                                  </td>
                                </tr>
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      // borderBottom: "1px solid #ddd",
                                      fontSize: "12px",
                                      width: "50%",
                                    }}
                                  >
                                    Total in AED
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      // borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  ></td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      // borderBottom: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {CommaSeparator(
                                      parseFloat(
                                        voucherDetail?.aed_total
                                      )?.toFixed(2)
                                    )}{" "}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ padding: "18px", marginTop: "5px" }}
                  >
                    <Grid
                      item
                      xs={12}
                      sx={{
                        border: "solid",
                        borderWidth: 0.5,
                        borderColor: "lightgray",
                        marginRight: "2px",
                        marginBottom: "10px",
                      }}
                    >
                      <Grid item xs={12}>
                        <Typography
                          variant="h5"
                          sx={{
                            textAlign: "center",
                            background:
                              "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                            color: Colors.whiteSmoke,
                            padding: "10px",
                            fontFamily: "Helvetica",
                            fontWeight: "bold",
                          }}
                        >
                          Remarks
                        </Typography>
                      </Grid>
                      <Grid container sx={{ padding: "10px" }}>
                        <Grid item xs={12}>
                          <Typography variant="body1" fontSize="12px">
                            {voucherDetail?.remarks}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container>
                  <Grid item xs={6} sx={{ pl: 0.2, py: 1 }}>
                    <Grid container sx={{ padding: "10px" }}>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            fontFamily: "Helvetica",
                          }}
                        >
                          <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                margin: 0,
                                padding: 0,
                                tableLayout: "fixed",
                              }}
                            >
                              <thead>
                                <tr
                                  style={{
                                    background:
                                      "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                                    color: Colors.whiteSmoke,
                                    fontWeight: "bold",
                                  }}
                                >
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      borderTop: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Total Amount $
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderTop: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Ex.Rate
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "2px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderTop: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Total Amount (AED)
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      borderLeft: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {voucherDetail?.usd_total
                                      ? CommaSeparator(
                                          parseFloat(
                                            voucherDetail?.aed_total /
                                              (voucherDetail?.received_ex_rate
                                                ? voucherDetail?.received_ex_rate
                                                : exchangeRateUsd)
                                          ).toFixed(2)
                                        )
                                      : CommaSeparator(
                                          parseFloat(0).toFixed(2)
                                        )}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {voucherDetail?.received_ex_rate
                                      ? parseFloat(
                                          voucherDetail?.received_ex_rate
                                        ).toFixed(3)
                                      : exchangeRateUsd}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      borderBottom: "1px solid #ddd",
                                      borderRight: "1px solid #ddd",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {CommaSeparator(
                                      parseFloat(
                                        voucherDetail?.aed_total
                                      )?.toFixed(2)
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container spacing={1} padding="30px">
                  <Grid item xs={10}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      PLEASE READ CAREFULLY THE T&C:
                    </Typography>
                    <Box
                      sx={{
                        fontWeight: 500,
                        fontSize: "12px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        className={classes.text}
                        sx={{ mb: 1 }}
                      >
                        1- Kindly check or review the invoice/voucher and cash
                        before leaving the counter.
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.text}
                        sx={{ mb: 1 }}
                      >
                        2- Our establishment will not be responsible for any
                        delay, mistakes, or omissions in transmission.
                      </Typography>{" "}
                      <Typography
                        variant="body2"
                        className={classes.text}
                        sx={{ mb: 1 }}
                      >
                        3- Our liability is limited to making all necessary
                        amendments in the shortest possible time.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={2} sm={2} md={2}>
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <QRCodeCanvas
                        value={
                          window.location.origin +
                          `/payment-voucher-preview/${btoa(id)}`
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ pl: 4, pr: 3, py: 1, backgroundColor: "#0C6135" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: Colors.white,
                      fontFamily: FontFamily.openSans,
                    }}
                  >
                    Customer care Contact: Mohammed husni - +971523195682
                    (Arabic & English ) Ardamehr Shoev - +971545836028 (English
                    ,Arabic, Tajik & Farsi)
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: Colors.white,
                      fontFamily: FontFamily.openSans,
                    }}
                  >
                    Ravin abdul kareem - +971528293801 (Kurdish , Arabic &
                    English) Magsat Gylyjov - +97158666403 (Turken , Russian &
                    English)
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Grid container spacing={0}>
                  <Grid item xs={3.5} sm={3.5} md={3.5}>
                    <Box
                      component={"img"}
                      src={LogoImage}
                      sx={{ width: "100%", alignItems: "center" }}
                    />
                  </Grid>
                  <Grid item xs={8.5} sm={8.5} md={8.5}>
                    <CardMedia image={Images.invoiceHeader} sx={{ mb: 2 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          py: 3,
                          textAlign: "center",
                          color: Colors.white,
                        }}
                      >
                        Galaxy Used Cars Tr. LLC
                      </Typography>
                    </CardMedia>
                    <Grid
                      container
                      spacing={1.5}
                      alignItems={"center"}
                      justifyContent={"space-evenly"}
                    >
                      <Grid item xs={4} sm={4} md={4}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "5px",
                            alignItems: "center",
                          }}
                        >
                          <InvoicePhone />
                          <Typography
                            variant="body1"
                            sx={{
                              color: Colors.smokeyGrey,
                              fontFamily: FontFamily.openSans,
                            }}
                          >
                            +971 6 510 2000
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={6} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "5px",
                            alignItems: "center",
                          }}
                        >
                          <InvoiceMail />
                          <Typography
                            variant="body1"
                            sx={{
                              color: Colors.smokeyGrey,
                              fontFamily: FontFamily.openSans,
                            }}
                          >
                            info@galaxyshipping.com
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} sm={4} md={4}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "5px",
                            alignItems: "center",
                          }}
                        >
                          <InvoiceGlobal />
                          <Typography
                            variant="body1"
                            sx={{
                              color: Colors.smokeyGrey,
                              fontFamily: FontFamily.openSans,
                            }}
                          >
                            https://galaxyshipping.com
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={6} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "5px",
                            alignItems: "center",
                          }}
                        >
                          <InvoiceLocation />
                          <Typography
                            variant="body1"
                            sx={{
                              color: Colors.smokeyGrey,
                              fontFamily: FontFamily.openSans,
                            }}
                          >
                            Ind Area#4 P.O Box 83126, Sharjah , UAE
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    my: 5,
                    position: "relative",
                    bgcolor: Colors.bluishCyan,
                    width: 1,
                    height: "12px",
                  }}
                >
                  <Typography
                    component={"span"}
                    variant="h2"
                    sx={{
                      color: Colors.charcoalGrey,
                      bgcolor: Colors.white,
                      p: 2,
                      letterSpacing: "3px",
                      position: "absolute",
                      right: "90px",
                      top: "-40px",
                    }}
                  >
                    PAYMENT VOUCHER
                  </Typography>
                </Box>
                <Grid
                  container
                  spacing={0}
                  justifyContent={"space-between"}
                  sx={{ mt: "65px" }}
                >
                  <Grid item xs={5.5} sm={5.5} md={5.5}>
                    <Box sx={{ ml: 4, mb: 0 }}>
                      <Grid container>
                        <Grid item xs={5} sm={5} md={5}>
                          <Typography
                            fontWeight={"bold"}
                            fontSize={17}
                            variant="body1"
                            color={"black"}

                            // className={classes.text}
                          >
                            To Account:
                          </Typography>
                        </Grid>
                        <Grid item xs={6.5} sm={6.5} md={6.5}>
                          <Typography
                            variant="body1"
                            // className={classes.text}
                            fontSize={17}
                            color={"black"}
                            ml={-8}
                          >
                            {voucherDetail?.account?.name}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box
                      sx={{
                        ml: 4,
                        mb: voucherDetail?.account?.name?.length > 25 ? 12 : 15,
                      }}
                    >
                      <Grid container>
                        <Grid item xs={5} sm={5} md={5}>
                          <Typography
                            fontWeight={"bold"}
                            fontSize={17}
                            variant="body1"
                            color={"black"}

                            // className={classes.text}
                          >
                            Account Code:
                          </Typography>
                        </Grid>
                        <Grid item xs={6.5} sm={6.5} md={6.5}>
                          <Typography
                            variant="body1"
                            // className={classes.text}
                            fontSize={17}
                            color={"black"}
                            ml={-6}
                          >
                            {voucherDetail?.account?.account_code}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box sx={{ mr: 3, mt: 1 }}></Box>
                    <Box
                      className={classes.headingBg}
                      sx={{ bgcolor: Colors.primary }}
                    >
                      <Typography variant="h4" className={classes.heading}>
                        Paid Currency Details
                      </Typography>
                    </Box>
                    <Box sx={{ mr: 3, mb: 8 }}>
                      <Grid container spacing={0} justifyContent={"flex-end"}>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{
                              textAlign: "right",
                              fontWeight: "bold !important",
                            }}
                          >
                            Currency
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{
                              textAlign: "right",
                              fontWeight: "bold !important",
                            }}
                          >
                            FCY
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{
                              textAlign: "right",
                              fontWeight: "bold !important",
                            }}
                          >
                            Ex. Rate
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{
                              textAlign: "right",
                              fontWeight: "bold !important",
                            }}
                          >
                            LCY
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "center" }}
                          >
                            AED
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {voucherDetail?.aed_amount_fcy
                              ? CommaSeparator(
                                  parseFloat(
                                    voucherDetail?.aed_amount_fcy
                                  )?.toFixed(2)
                                )
                              : "0.00"}
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {CommaSeparator(parseFloat(1)?.toFixed(2))}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {voucherDetail?.aed_amount_lcy
                              ? CommaSeparator(
                                  parseFloat(
                                    voucherDetail?.aed_amount_lcy
                                  )?.toFixed(2)
                                )
                              : "0.00"}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "center" }}
                          >
                            USD
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {voucherDetail?.usd_amount_fcy
                              ? CommaSeparator(
                                  parseFloat(
                                    voucherDetail?.usd_amount_fcy
                                  )?.toFixed(2)
                                )
                              : "0.00"}
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {voucherDetail?.updated_ex_rate
                              ? parseFloat(
                                  voucherDetail?.updated_ex_rate
                                ).toFixed(3)
                              : "0.00"}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {voucherDetail?.usd_amount_lcy
                              ? CommaSeparator(
                                  parseFloat(voucherDetail?.usd_amount_lcy)
                                )
                              : "0.00"}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{
                              textAlign: "left",
                              fontWeight: "bold !important",
                            }}
                          >
                            Total in AED
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {CommaSeparator(
                              parseFloat(voucherDetail?.aed_total)?.toFixed(2)
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box
                      className={classes.headingBg}
                      sx={{ bgcolor: Colors.bluishCyan }}
                    >
                      <Typography variant="h4" className={classes.heading}>
                        Remarks
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 4, mr: 2 }}>
                      <Typography variant="body1" className={classes.text}>
                        {voucherDetail?.remarks}
                      </Typography>
                    </Box>
                    <Box
                      className={classes.headingBg}
                      sx={{
                        pl: "32px !important",
                        bgcolor: Colors.aliceBlue,
                      }}
                    >
                      <Typography
                        variant="body1"
                        className={classes.heading}
                        sx={{
                          textAlign: "left",
                          color: `${Colors.charcoalGrey} !important`,
                        }}
                      >
                        <b>Paid by :</b>
                        <Typography
                          component={"span"}
                          variant="body2"
                          sx={{ textTransform: "none" }}
                        >
                          &nbsp; {voucherDetail?.creator?.name}
                        </Typography>
                      </Typography>
                    </Box>
                    {voucherDetail?.reversed_by && (
                      <Box
                        className={classes.headingBg}
                        sx={{
                          pl: "32px !important",
                          bgcolor: Colors.aliceBlue,
                        }}
                      >
                        <Typography
                          variant="body1"
                          className={classes.heading}
                          sx={{
                            textAlign: "left",
                            color: `${Colors.charcoalGrey} !important`,
                          }}
                        >
                          <b>Reversed by :</b>
                          <Typography
                            component={"span"}
                            variant="body2"
                            sx={{ textTransform: "none" }}
                          >
                            &nbsp; {voucherDetail?.reversed_by}
                          </Typography>
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={5.5} sm={5.5} md={5.5}>
                    <Box sx={{ mr: 3, mt: 0, mb: 2, height: "100px" }}>
                      <Grid container spacing={0} justifyContent={"flex-end"}>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            Payment Voucher #:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            GPV-{voucherDetail?.id}
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            Branch:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            {voucherDetail?.branch?.name ?? "-"}
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            Processing Date:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            {moment(voucherDetail?.created_at).format(
                              "DD-MMM-YYYY hh:mm"
                            )}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            Customize Date:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            {moment(voucherDetail?.c_date).format(
                              "DD-MMM-YYYY hh:mm"
                            )}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            Last Updated on:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          className={classes.tableCell}
                        >
                          <Typography variant="body1" className={classes.text}>
                            {moment(voucherDetail?.updated_at).format(
                              "DD-MMM-YYYY hh:mm"
                            )}
                          </Typography>
                        </Grid>
                        {/* <Grid
                                                item
                                                xs={6}
                                                sm={6}
                                                md={6}
                                                className={classes.tableCell}
                                            >
                                                <Typography variant="body1" className={classes.text}>
                                                    Account / Child Account
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={6}
                                                sm={6}
                                                md={6}
                                                className={classes.tableCell}
                                            >
                                                <Typography variant="body1" className={classes.text}>
                                                    {voucherDetail?.account?.name}
                                                </Typography>
                                            </Grid> */}
                      </Grid>
                    </Box>
                    <Box
                      className={classes.headingBg}
                      sx={{ bgcolor: Colors.primary, mt: 2 }}
                    >
                      <Typography variant="h4" className={classes.heading}>
                        Amount Paid
                      </Typography>
                    </Box>
                    <Box sx={{ mr: 3, mb: 2 }}>
                      <Grid container spacing={0} justifyContent={"flex-end"}>
                        <Grid
                          item
                          xs={4}
                          sm={4}
                          md={4}
                          className={classes.tableCell}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{
                              textAlign: "right",
                              fontWeight: "bold !important",
                            }}
                          >
                            AED
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sm={4}
                          md={4}
                          className={classes.tableCell}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{
                              textAlign: "right",
                              fontWeight: "bold !important",
                            }}
                          >
                            Ex. Rate
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sm={4}
                          md={4}
                          className={classes.tableCell}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{
                              textAlign: "right",
                              fontWeight: "bold !important",
                            }}
                          >
                            USD
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sm={4}
                          md={4}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {CommaSeparator(
                              parseFloat(voucherDetail?.aed_total)?.toFixed(2)
                            )}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sm={4}
                          md={4}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {voucherDetail?.received_ex_rate
                              ? parseFloat(
                                  voucherDetail?.received_ex_rate
                                ).toFixed(3)
                              : exchangeRateUsd}
                          </Typography>
                        </Grid>

                        <Grid
                          item
                          xs={4}
                          sm={4}
                          md={4}
                          className={classes.tableCell}
                          sx={{ backgroundColor: "white !important" }}
                        >
                          <Typography
                            variant="body1"
                            className={classes.text}
                            sx={{ textAlign: "right" }}
                          >
                            {voucherDetail?.usd_total
                              ? CommaSeparator(
                                  parseFloat(
                                    voucherDetail?.aed_total /
                                      (voucherDetail?.received_ex_rate
                                        ? voucherDetail?.received_ex_rate
                                        : exchangeRateUsd)
                                  ).toFixed(2)
                                )
                              : CommaSeparator(parseFloat(0).toFixed(2))}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    {voucherDetail?.payment_medium == "bank" ? (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        // className={classes.tableCell}
                        sx={{ backgroundColor: "white !important", mb: 2 }}
                      >
                        <Typography
                          variant="body1"
                          className={classes.text}
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold !important",
                          }}
                        >
                          Bank Account
                        </Typography>
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        // className={classes.tableCell}
                        sx={{ backgroundColor: "white !important", mb: 2 }}
                      >
                        <Typography
                          variant="body1"
                          className={classes.text}
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold !important",
                          }}
                        >
                          Cashier Accounts
                        </Typography>
                      </Grid>
                    )}

                    <Box sx={{ mr: 3, mb: 8 }}>
                      <Grid container spacing={0} justifyContent={"flex-end"}>
                        {voucherDetail?.bank && (
                          <>
                            <Grid
                              item
                              xs={9}
                              sm={9}
                              md={9}
                              className={classes.tableCell}
                              sx={{ backgroundColor: "white !important" }}
                            >
                              <Typography
                                variant="body1"
                                className={classes.text}
                                sx={{ textAlign: "right" }}
                              >
                                {voucherDetail?.bank?.name}
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={3}
                              sm={3}
                              md={3}
                              className={classes.tableCell}
                              sx={{ backgroundColor: "white !important" }}
                            >
                              <Typography
                                variant="body1"
                                className={classes.text}
                                sx={{ textAlign: "right" }}
                              >
                                {voucherDetail?.usd_amount_fcy > 0
                                  ? CommaSeparator(
                                      parseFloat(
                                        voucherDetail?.usd_amount_fcy
                                      )?.toFixed(2)
                                    )
                                  : CommaSeparator(
                                      parseFloat(
                                        voucherDetail?.aed_amount_fcy
                                      )?.toFixed(2)
                                    )}
                              </Typography>
                            </Grid>
                          </>
                        )}

                        {!voucherDetail?.bank && (
                          <>
                            {voucherDetail?.cashier_aed && (
                              <>
                                <Grid
                                  item
                                  xs={9.5}
                                  sm={9.5}
                                  md={9.5}
                                  className={classes.tableCell}
                                  sx={{ backgroundColor: "white !important" }}
                                >
                                  <Typography
                                    variant="body1"
                                    className={classes.text}
                                    sx={{ textAlign: "center" }}
                                  >
                                    {voucherDetail?.cashier_aed?.name}
                                  </Typography>
                                </Grid>
                                <Grid
                                  item
                                  xs={2.5}
                                  sm={2.5}
                                  md={2.5}
                                  className={classes.tableCell}
                                  sx={{ backgroundColor: "white !important" }}
                                >
                                  <Typography
                                    variant="body1"
                                    className={classes.text}
                                    sx={{ textAlign: "center" }}
                                  >
                                    {voucherDetail?.cashier_aed
                                      ? CommaSeparator(
                                          parseFloat(
                                            voucherDetail?.aed_amount_lcy
                                          )?.toFixed(2)
                                        )
                                      : ""}
                                  </Typography>
                                </Grid>
                              </>
                            )}
                            {voucherDetail?.cashier_usd && (
                              <>
                                <Grid
                                  item
                                  xs={9.5}
                                  sm={9.5}
                                  md={9.5}
                                  className={classes.tableCell}
                                  sx={{ backgroundColor: "white !important" }}
                                >
                                  <Typography
                                    variant="body1"
                                    className={classes.text}
                                    sx={{ textAlign: "center" }}
                                  >
                                    {voucherDetail?.cashier_usd?.name}
                                  </Typography>
                                </Grid>
                                <Grid
                                  item
                                  xs={2.5}
                                  sm={2.5}
                                  md={2.5}
                                  className={classes.tableCell}
                                  sx={{ backgroundColor: "white !important" }}
                                >
                                  <Typography
                                    variant="body1"
                                    className={classes.text}
                                    sx={{ textAlign: "right" }}
                                  >
                                    {voucherDetail?.cashier_usd
                                      ? CommaSeparator(
                                          parseFloat(
                                            voucherDetail?.usd_amount_fcy
                                          )?.toFixed(2)
                                        )
                                      : ""}
                                  </Typography>
                                </Grid>
                              </>
                            )}
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  padding={2}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  {/* <Grid item xs={9.5} sm={9.5} md={9.5}>
                                <Box sx={{ pl: 4, pr: 3, mb: 3, mt: 4 }}>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        PLEASE READ CAREFULLY BELOW TERM & CONDITION:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={classes.text}
                                        sx={{ mb: 1 }}
                                    >
                                        1 - I've clearly informed and the make the understand all
                                        the vehicle information, amount, charges and rates.
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={classes.text}
                                        sx={{ mb: 1 }}
                                    >
                                        2 - Kindly pay the due amount within 3 business days from
                                        the purchase date to avoid the Late Payment and Storages
                                        that will be charged once vehicle arrived to final
                                        destination (Further, If there are some special
                                        annousment/memo ignore this and follow that)
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={classes.text}
                                        sx={{ mb: 1 }}
                                    >
                                        3 - If vehicle got relisted, the relist charges customer has
                                        to pay within 3 days otherwise 15% Penalty will applied
                                        after 3 days as issued memo on 9/Jun/2022.
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={classes.text}
                                        sx={{ mb: 1 }}
                                    >
                                        4 - Galaxy Customer care department will inform you about
                                        the latest updates about rates and charges through WhatsApp
                                        and emails.
                                    </Typography>
                                </Box>
                            </Grid> */}
                  <Grid item xs={10}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      PLEASE READ CAREFULLY THE T&C:
                    </Typography>
                    <Box
                      sx={{
                        fontWeight: 500,
                        fontSize: "12px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        className={classes.text}
                        sx={{ mb: 1 }}
                      >
                        1- Kindly check or review the invoice/voucher and cash
                        before leaving the counter.
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.text}
                        sx={{ mb: 1 }}
                      >
                        2- Our establishment will not be responsible for any
                        delay, mistakes, or omissions in transmission.
                      </Typography>{" "}
                      <Typography
                        variant="body2"
                        className={classes.text}
                        sx={{ mb: 1 }}
                      >
                        3- Our liability is limited to making all necessary
                        amendments in the shortest possible time.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={2} sm={2} md={2}>
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <QRCodeCanvas
                        value={
                          window.location.origin +
                          `/payment-voucher-preview/${btoa(id)}`
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ pl: 4, pr: 3, py: 1, bgcolor: Colors.primary }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: Colors.white,
                      fontFamily: FontFamily.openSans,
                    }}
                  >
                    Customer care Contact: Mohammed husni - +971523195682
                    (Arabic & English ) Ardamehr Shoev - +971545836028 (English
                    ,Arabic, Tajik & Farsi)
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: Colors.white,
                      fontFamily: FontFamily.openSans,
                    }}
                  >
                    Ravin abdul kareem - +971528293801 (Kurdish , Arabic &
                    English) Magsat Gylyjov - +97158666403 (Turken , Russian &
                    English)
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </div>
    </Container>
  );
}

export default PaymentVoucherInvoice;
